import { MB_SVG_Check, MB_SVG_ChevronDown, MB_SVG_ChevronUp, MB_SVG_X } from '@/components/general/svg-components';
import { MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { isMobile } from '@/utils/general/device-info';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import React, { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { FlatList, FlatListProps, StyleProp, StyleSheet, Text, TouchableOpacity, TouchableOpacityProps, View, ViewProps, ViewStyle, TextStyle, Platform } from 'react-native';
import RNDropDownPicker, {
  ValueType,
  ItemType,
  RenderListItemPropsInterface,
  RenderBadgeItemPropsInterface,
  DropDownDirectionType,
  ListEmptyComponentPropsInterface,
} from 'react-native-dropdown-picker';

// TODO: Hovo: Review
// TODO: Something is wrong with types here, we should not use all of these `as` keywords.

interface MB_DropDownPickerBaseProps<T> {
  /**
   * The style for the component.
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * The dropdown picker container style.
   */
  dropdownContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The dropdown picker style.
   */
  dropdownStyle?: StyleProp<ViewStyle>;

  /**
   * The color of the selected option.
   *
   * Default: "#FFFFFF"
   */
  selectedOptionColor?: string;

  /**
   * The style of the search container.
   */
  searchContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the search text input.
   */
  searchTextInput?: StyleProp<TextStyle>;

  /**
   * The style of the placeholder.
   */
  placeholderStyle?: StyleProp<TextStyle>;

  /**
   * The style of the dropdown label.
   */
  dropdownLabelStyle?: StyleProp<TextStyle>;

  /**
   * The style of the list message container.
   */
  listMessageContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the list message text.
   */
  listMessageTextStyle?: StyleProp<TextStyle>;

  /**
   * General style.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * Dropdown options.
   */
  items: ItemType<T>[];

  /**
   * The title of the dropdown picker.
   */
  title?: string;

  /**
   * The placeholder of the dropdown picker.
   */
  placeholder?: string;

  /**
   * The color of the highlighted option.
   *
   * Default: '#452A4C'
   */
  highlightColor?: string;

  /**
   * The style of the title.
   */
  titleStyle?: StyleProp<TextStyle>;

  /**
   * The style of the label.
   */
  labelStyle?: StyleProp<TextStyle>;

  /**
   * Enable search. A search bar will show up.
   */
  searchable?: boolean;

  /**
   * Disable search on local items.
   */
  disableLocalSearch?: boolean;

  /**
   * Callback when the search text changes.
   */
  onChangeSearchText?: (text: string) => void;

  /**
   * The color of the arrow.
   */
  arrowColor?: string;

  /**
   * The arrow's style.
   */
  arrowStyle?: StyleProp<ViewStyle>;

  /**
   * The z-index of the dropdown picker.
   */
  zIndex?: number;

  /**
   * The badge component.
   */
  badge?: (props: RenderBadgeItemPropsInterface<T>) => React.JSX.Element;

  /**
   * The badge's text style.
   */
  badgeTextStyle?: StyleProp<TextStyle>;

  /**
   * The badge's style.
   */
  badgeStyle?: StyleProp<ViewStyle>;

  /**
   * The badge's touchable container style.
   */
  badgeTouchableContainer?: StyleProp<ViewStyle>;

  /**
   * The badge's icon.
   */
  badgeIcon?: ReactNode;

  /**
   * The list mode.
   *
   * Note: The `FlatList` component shouldn't be nested inside a `ScrollView` or
   * you'll come across the
   *
   *  > VirtualizedLists should never be nested inside plain ScrollViews
   *
   * warning. If this happens to you and you only have a few items, consider
   * using the `SCROLLVIEW` mode. Otherwise you have to use the `MODAL` mode.
   */
  listMode?: 'DEFAULT' | 'FLATLIST' | 'SCROLLVIEW' | 'MODAL';

  /**
   * The item separator style.
   */
  itemSeparatorStyle?: StyleProp<ViewStyle>;

  /**
   * Disable interactions with the component.
   */
  disableInteractions?: boolean;
  showRequiredAsteriks?: boolean;
  requiredAsteriksStyle?: StyleProp<TextStyle>;
  disabled?: boolean;
  dropDownDirection?: DropDownDirectionType;
  customListItem?: (props: RenderListItemPropsInterface<T>) => React.JSX.Element;
  /**
   * set the badges to be as flex-wrap style
   */
  extendableBadgeContainer?: boolean;
  listEmptyMessage?: string;
}

type MB_DropDownPickerDefaultProps = Omit<
  MB_DropDownPickerBaseProps<ValueType>,
  | 'dropDownDirection'
  | 'selectedOptionColor'
  | 'items'
  | 'title'
  | 'placeholder'
  | 'searchable'
  | 'disableLocalSearch'
  | 'onChangeSearchText'
  | 'zIndex'
  | 'badge'
  | 'badgeIcon'
  | 'listMode'
  | 'customListItem'
  | 'extendableBadgeContainer'
>;

const MB_DropDownPickerSetDefaultStyles = (props: MB_DropDownPickerDefaultProps) => {
  defaultProps = props;
};

let defaultProps: MB_DropDownPickerDefaultProps | undefined;

interface MB_DropDownPickerMultipleProps<T> {
  /**
   * Whether to allow multiple selection or not.
   */
  multiple: true;

  /**
   * Callback when the value changes.
   */
  onValueChange?: (value: T[] | null) => void;

  /**
   * Callback when the item is selected.
   */
  onItemChange?: (items: ItemType<T>[]) => void;

  /**
   * The initial values.
   */
  value?: T[];
}

interface MB_DropDownPickerSingleProps<T> {
  /**
   * Whether to allow multiple selection or not.
   */
  multiple?: false;

  /**
   * Callback when the value changes.
   */
  onValueChange?: (value: T | null) => void;

  /**
   * Callback when the item is selected.
   */
  onItemChange?: (item: ItemType<T>) => void;

  /**
   * The initial value.
   */
  value?: T;
  dropDownDirection?: DropDownDirectionType;
}

type MB_DropDownPickerProps<T> = (MB_DropDownPickerSingleProps<T> | MB_DropDownPickerMultipleProps<T>) & MB_DropDownPickerBaseProps<T>;

const Arrow = ({ direction, color, style }: { direction: 'up' | 'down'; color?: string; style?: StyleProp<ViewStyle> }) => {
  return (
    <View style={[styles.arrow, style]}>
      {direction === 'down' && <MB_SVG_ChevronDown color={color ?? '#FFFFFF'} size={15} />}
      {direction === 'up' && <MB_SVG_ChevronUp color={color ?? '#FFFFFF'} size={15} />}
    </View>
  );
};

const _MB_DropDownPicker = <T extends ValueType>({
  value,
  items,
  onValueChange,
  onItemChange,
  containerStyle,
  dropdownContainerStyle,
  dropdownStyle,
  selectedOptionColor,
  searchContainerStyle,
  searchTextInput,
  dropdownLabelStyle,
  placeholderStyle,
  listMessageContainerStyle,
  listMessageTextStyle,
  listEmptyMessage,
  style,
  title,
  placeholder,
  highlightColor = '#452A4C',
  titleStyle,
  labelStyle,
  searchable,
  onChangeSearchText,
  disableLocalSearch,
  multiple,
  arrowColor,
  arrowStyle,
  zIndex,
  badge,
  badgeStyle,
  badgeTextStyle,
  badgeTouchableContainer,
  badgeIcon,
  listMode,
  itemSeparatorStyle,
  disableInteractions,
  showRequiredAsteriks,
  requiredAsteriksStyle,
  disabled,
  dropDownDirection,
  customListItem,
  extendableBadgeContainer,
}: MB_DropDownPickerProps<T>) => {
  const [open, setOpen] = useState(false);
  const [currentValue, setCurrentValue] = useState<T | T[] | null>(value ?? null);
  const [highlightedValue, setHighlightedValue] = useState<T>();
  const mouseInsideModal = useRef(false);
  const flatListRef = useRef<FlatList>(null);

  const ArrowUp = useCallback(() => <Arrow direction="up" color={arrowColor} style={arrowStyle} />, [arrowColor, arrowStyle]);
  const ArrowDown = useCallback(() => <Arrow direction="down" color={arrowColor} style={arrowStyle} />, [arrowColor, arrowStyle]);

  const onModalClose = useCallback(() => {
    setHighlightedValue(undefined);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: any) => (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'Enter') && e.preventDefault();
    const onMouseClick = () => {
      if (!mouseInsideModal.current) {
        setOpen(false);
        onModalClose();
      }
    };
    const disableKeys = () => (isMobile ? undefined : MB_customWindowForWeb.removeEventListener('keydown', onKeyDown));
    const enableKeys = () => (isMobile ? undefined : MB_customWindowForWeb.addEventListener('keydown', onKeyDown));
    const enableMouseClick = () => (isMobile ? undefined : MB_customWindowForWeb.addEventListener('click', onMouseClick));
    const disableMouseClick = () => (isMobile ? undefined : MB_customWindowForWeb.removeEventListener('click', onMouseClick));
    if (open) {
      enableMouseClick();
      enableKeys();
    } else {
      disableKeys();
      disableMouseClick();
    }
    return () => {
      disableKeys();
      disableMouseClick();
    };
  }, [onModalClose, open]);

  const onModalOpen = useCallback(() => {
    setTimeout(() => {
      if (!Array.isArray(currentValue) && items.length > 0) {
        const values = items.map(item => item.value);
        const index = values.indexOf(currentValue as T | undefined);
        flatListRef.current?.scrollToIndex({ index: index === -1 ? 0 : index, viewPosition: 0.5 });
      }
    }, 100);
  }, [currentValue, items]);

  const renderBadgeItem = useCallback(
    (item: RenderBadgeItemPropsInterface<T>) => {
      return (
        <TouchableOpacity style={[item.badgeStyle, styles.badgeStyle, badgeStyle]} activeOpacity={1}>
          <Text style={[item.badgeTextStyle, styles.badgeTextStyle, badgeTextStyle]}>{item.label}</Text>
          <TouchableOpacity {...item.props} style={[styles.badgeTouchableContainer, badgeTouchableContainer]} onPress={() => item.onPress(item.value)}>
            {badgeIcon ?? <MB_SVG_X color="#333333" size={6} />}
          </TouchableOpacity>
        </TouchableOpacity>
      );
    },
    [badgeIcon, badgeStyle, badgeTextStyle, badgeTouchableContainer]
  );

  const renderItem = useCallback(
    ({ item, value: itemValue, label, isSelected, onPress }: RenderListItemPropsInterface<T>) => {
      const isHighlighted = highlightedValue === itemValue;
      const props = {
        onMouseMove: () => setHighlightedValue(itemValue),
        onPress: () => onPress(item as T),
        style: {
          backgroundColor: isHighlighted || (isSelected && !multiple) ? mbApplyTransparency(highlightColor, isSelected ? 1 : 0.5) : undefined,
          paddingHorizontal: 27,
          paddingVertical: 4.5,
        },
      };
      return (
        <TouchableOpacity {...props} style={[props.style, multiple && styles.multipleSelect]}>
          <Text style={[styles.labelStyle, labelStyle]}>{label}</Text>
          {multiple && isSelected && <MB_SVG_Check color={selectedOptionColor ?? '#FFFFFF'} size={15} />}
        </TouchableOpacity>
      );
    },
    [highlightColor, highlightedValue, labelStyle, multiple, selectedOptionColor]
  );

  const dropdownProps = useMemo<TouchableOpacityProps>(
    () => ({
      onBlur: () => {
        // NOTE: we need to handle the case where multiple is true
        // and clicking outside the modol should close the dropdown
        if (isMobile) {
          if (mouseInsideModal.current === false) {
            setOpen(false);
            onModalClose();
          } else {
            mouseInsideModal.current = false;
          }
        }
      },
      onKeyUp: (e: any) => {
        e.preventDefault();
        const values = items.map(item => item.value);
        let index = values.indexOf(highlightedValue);
        if (index === -1 && !multiple) {
          index = values.indexOf(currentValue as T);
        }
        if (e.code === 'ArrowDown' && values.length > 0) {
          const toIndex = index === -1 || index + 1 >= values.length ? 0 : index + 1;
          setHighlightedValue(values[toIndex]);
          flatListRef.current?.scrollToIndex({ index: toIndex, viewPosition: 0.5 });
        } else if (e.code === 'ArrowUp' && values.length > 0) {
          const toIndex = index === -1 || index - 1 < 0 ? values.length - 1 : index - 1;
          setHighlightedValue(values[toIndex]);
          flatListRef.current?.scrollToIndex({ index: toIndex, viewPosition: 0.5 });
        } else if (e.code === 'Enter' && highlightedValue) {
          setHighlightedValue(undefined);
          if (multiple) {
            setCurrentValue(oldValue => {
              if (!oldValue) {
                return [highlightedValue];
              } else if (!(oldValue as T[]).includes(highlightedValue)) {
                return [...(oldValue as T[]), highlightedValue];
              } else {
                return (oldValue as T[]).filter(el => el !== highlightedValue);
              }
            });
          } else {
            setCurrentValue(highlightedValue);
          }
        } else if (e.code === 'Escape') {
          setOpen(false);
          setHighlightedValue(undefined);
        }
      },
      activeOpacity: 1,
    }),
    [onModalClose, items, highlightedValue, multiple, currentValue]
  );

  const containerProps = useMemo(
    () =>
      ({
        onMouseMove: () => (mouseInsideModal.current = true),
        onMouseLeave: () => (mouseInsideModal.current = false),
      } as ViewProps),
    []
  );

  const flatListProps = useMemo(
    () =>
      ({
        ref: flatListRef,
        onScrollToIndexFailed: () => {},
        nestedScrollEnabled: true,
      } as Partial<FlatListProps<ItemType<T>>>),
    []
  );

  const EmptyListComponent = useCallback(
    (props: ListEmptyComponentPropsInterface) => {
      const { listMessageTextStyle: localListMessageTextStyle, ActivityIndicatorComponent, loading, message } = props;
      // Note: react-native-dropdown-picker don't properly exposes listMessageContainerStyle.
      // ListEmptyComponentPropsInterface has listMessageContainer property, which is not working properly.
      return (
        <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1, padding: 10 }}>
          {loading ? { ActivityIndicatorComponent } : <Text style={localListMessageTextStyle}>{listEmptyMessage ?? message}</Text>}
        </View>
      );
    },
    [listEmptyMessage]
  );

  return (
    <View style={[Platform.OS === 'ios' && { zIndex: zIndex ?? 1 }, containerStyle]} pointerEvents={disableInteractions ? 'none' : undefined}>
      {title && (
        <Text style={[styles.title, titleStyle]}>
          {title}
          {showRequiredAsteriks && <Text style={requiredAsteriksStyle ?? styles.requiredAsteriksColor}>*</Text>}
        </Text>
      )}
      {multiple === true && (
        <RNDropDownPicker
          multiple={true}
          placeholder={placeholder}
          open={open}
          setOpen={setOpen}
          searchable={searchable}
          onChangeSearchText={onChangeSearchText}
          disableLocalSearch={disableLocalSearch}
          items={items}
          value={currentValue as null}
          props={dropdownProps}
          containerProps={containerProps}
          onChangeValue={onValueChange}
          onSelectItem={onItemChange}
          setValue={setCurrentValue}
          style={[styles.style, { outline: 'none' } as ViewStyle, style]}
          containerStyle={[style && StyleSheet.flatten(style).width !== undefined && { width: StyleSheet.flatten(style).width }, dropdownStyle]}
          extendableBadgeContainer={extendableBadgeContainer}
          labelStyle={styles.label}
          placeholderStyle={[styles.placeholder, placeholderStyle]}
          arrowIconStyle={styles.arrowIcon}
          arrowIconContainerStyle={disabled ? styles.arrowIconContainerDisabled : undefined}
          ArrowDownIconComponent={ArrowDown}
          ArrowUpIconComponent={ArrowUp}
          searchContainerStyle={[styles.searchContainer, searchContainerStyle]}
          searchTextInputStyle={[styles.searchTextInput, searchTextInput]}
          dropDownContainerStyle={[
            styles.dropDownContainer,
            style && StyleSheet.flatten(style).width !== undefined && { width: StyleSheet.flatten(style).width },
            dropdownContainerStyle,
          ]}
          modalContentContainerStyle={styles.modalContentContainer}
          listMessageContainerStyle={[styles.listMessageContainer, listMessageContainerStyle]}
          listMessageTextStyle={[styles.listMessageText, listMessageTextStyle]}
          ListEmptyComponent={EmptyListComponent}
          itemSeparatorStyle={[styles.itemSeparator, itemSeparatorStyle]}
          itemSeparator
          modalTitleStyle={styles.modalTitle}
          renderListItem={customListItem ?? renderItem}
          flatListProps={flatListProps}
          mode="BADGE"
          zIndex={zIndex ?? (Platform.OS === 'ios' ? 1 : undefined)}
          renderBadgeItem={badge ?? renderBadgeItem}
          listMode={listMode}
          disabled={disabled}
          dropDownDirection={dropDownDirection ?? 'AUTO'}
        />
      )}
      {!multiple && (
        <RNDropDownPicker
          multiple={false}
          placeholder={placeholder}
          open={open}
          setOpen={setOpen}
          searchable={searchable}
          onChangeSearchText={onChangeSearchText}
          disableLocalSearch={disableLocalSearch}
          items={items}
          value={currentValue as T | null}
          props={dropdownProps}
          containerProps={containerProps}
          onChangeValue={onValueChange as ((value: T | null) => void) | undefined}
          onSelectItem={onItemChange as ((item: ItemType<T>) => void) | undefined}
          setValue={setCurrentValue}
          style={[styles.style, { outline: 'none' } as ViewStyle, style]}
          containerStyle={[style && StyleSheet.flatten(style).width !== undefined && { width: StyleSheet.flatten(style).width }, dropdownStyle]}
          labelStyle={[styles.label, dropdownLabelStyle]}
          labelProps={{ numberOfLines: 1 }}
          placeholderStyle={[styles.placeholder, placeholderStyle]}
          arrowIconStyle={styles.arrowIcon}
          arrowIconContainerStyle={disabled ? styles.arrowIconContainerDisabled : undefined}
          ArrowDownIconComponent={ArrowDown}
          ArrowUpIconComponent={ArrowUp}
          searchContainerStyle={[styles.searchContainer, searchContainerStyle]}
          searchTextInputStyle={[styles.searchTextInput, searchTextInput]}
          dropDownContainerStyle={[
            styles.dropDownContainer,
            style && StyleSheet.flatten(style).width !== undefined && { width: StyleSheet.flatten(style).width },
            dropdownContainerStyle,
          ]}
          modalContentContainerStyle={styles.modalContentContainer}
          listMessageContainerStyle={[styles.listMessageContainer, listMessageContainerStyle]}
          listMessageTextStyle={[styles.listMessageText, listMessageTextStyle]}
          ListEmptyComponent={EmptyListComponent}
          itemSeparatorStyle={[styles.itemSeparator, itemSeparatorStyle]}
          itemSeparator
          modalTitleStyle={styles.modalTitle}
          renderListItem={customListItem ?? renderItem}
          flatListProps={flatListProps}
          zIndex={zIndex ?? (Platform.OS === 'ios' ? 1 : undefined)}
          renderBadgeItem={badge ?? renderBadgeItem}
          listMode={listMode}
          disabled={disabled}
          dropDownDirection={dropDownDirection ?? 'AUTO'}
          onClose={onModalClose}
          onOpen={onModalOpen}
        />
      )}
    </View>
  );
};

const MB_DropDownPicker = <T extends ValueType>(props: MB_DropDownPickerProps<T>) => {
  if (defaultProps) {
    const newProps: MB_DropDownPickerDefaultProps = {};
    Object.keys(defaultProps).forEach(key => {
      const stylePropsKey = key as keyof MB_DropDownPickerDefaultProps;

      if (stylePropsKey === 'disableInteractions' || stylePropsKey === 'showRequiredAsteriks' || stylePropsKey === 'disabled') {
        // TODO: We need a smarter way of copying these things.
        newProps[stylePropsKey] = props[stylePropsKey] ?? defaultProps?.[stylePropsKey];
      } else if (stylePropsKey === 'highlightColor' || stylePropsKey === 'arrowColor' || stylePropsKey === 'listEmptyMessage') {
        newProps[stylePropsKey] = props[stylePropsKey] ?? defaultProps?.[stylePropsKey];
      } else {
        newProps[stylePropsKey] = props[stylePropsKey] ? [defaultProps?.[stylePropsKey], props[stylePropsKey]] : defaultProps?.[stylePropsKey];
      }
    });

    return <_MB_DropDownPicker {...props} {...newProps} />;
  }

  return <_MB_DropDownPicker {...props} />;
};

export { MB_DropDownPicker, MB_DropDownPickerSetDefaultStyles };
export type {
  MB_DropDownPickerProps,
  MB_DropDownPickerSingleProps,
  MB_DropDownPickerMultipleProps,
  MB_DropDownPickerBaseProps,
  DropDownDirectionType,
  RenderBadgeItemPropsInterface,
  RenderListItemPropsInterface,
};

const styles = StyleSheet.create({
  title: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 28,
    color: '#FFFFFF',
    textAlign: 'center',
    alignSelf: 'flex-start',
  },
  labelStyle: {
    textAlign: 'left',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 28,
    color: '#FFFFFF',
  },
  arrow: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: mbApplyTransparency('#FFFFFF', 0.07),
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: '100%',
    zIndex: 1,
  },
  style: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 50,
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 6,
    borderColor: mbApplyTransparency('#BAC0CA', 0.4),
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 28,
    color: '#FFFFFF',
    flex: 1,
    textAlign: 'left',
  },
  placeholder: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 28,
    flex: 1,
    textAlign: 'left',
    color: '#bfc7d5',
  },
  labelContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  arrowIcon: {
    width: 20,
    height: 20,
  },
  tickIcon: {
    width: 20,
    height: 20,
  },
  closeIcon: {
    width: 30,
    height: 30,
  },
  listBody: {
    height: '100%',
  },
  listBodyContainer: {
    flexGrow: 1,
    alignItems: 'center',
  },
  dropDownContainer: {
    position: 'absolute',
    backgroundColor: '#1D1A31',
    borderRadius: 8,
    borderWidth: 0,
    width: '100%',
    overflow: 'hidden',
    zIndex: 1000,
  },
  modalContentContainer: {
    flexGrow: 1,
    backgroundColor: '#1D1A31',
  },
  listItemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    height: 40,
  },
  listItemLabel: {
    flex: 1,
    color: '#bfc7d5',
  },
  iconContainer: {
    marginRight: 10,
  },
  arrowIconContainerDisabled: {
    display: 'none',
  },
  tickIconContainer: {
    marginLeft: 10,
  },
  closeIconContainer: {
    marginLeft: 10,
  },
  listChildContainer: {
    paddingLeft: 40,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderBottomColor: '#565E67',
    borderBottomWidth: 1,
  },
  searchTextInput: {
    flexGrow: 1,
    flexShrink: 1,
    margin: 0,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderColor: '#565E67',
    borderWidth: 1,
    color: '#FFFFFF',
  },
  itemSeparator: {
    height: 0,
    backgroundColor: '#565E67',
  },
  flatListContentContainer: {
    flexGrow: 1,
  },
  customItemLabel: {
    fontStyle: 'italic',
  },
  listMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  listMessageText: {
    color: '#bfc7d5',
  },
  modalTitle: {
    fontSize: 18,
    color: '#bfc7d5',
  },
  extendableBadgeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
  },
  extendableBadgeItemContainer: {
    marginVertical: 3,
    marginEnd: 7,
  },
  badgeStyle: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    backgroundColor: '#dfdfdf',
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  multipleSelect: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badgeTouchableContainer: {
    borderWidth: 1,
    borderColor: '#333333',
    borderRadius: 360,
    padding: 3,
  },
  badgeTextStyle: {
    fontSize: 10,
    marginEnd: 6,
  },
  requiredAsteriksColor: {
    color: '#DD3636',
  },
});
